import { useFetcher, useLocation } from "@remix-run/react";
import type { SerializeFrom } from '@remix-run/node'
import { z } from "zod";
import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { Button } from "~/components/ui/button";
import { Field, CheckboxField } from '~/components/forms';
import { Send } from "lucide-react";
import { AuthenticityTokenInput } from "remix-utils/csrf/react";
import { useCategories } from "~/utils/hooks/useCategories";
import { useState } from "react";
import { NewsletterSubscribeResponse } from "~/.server/api/routers/form";

const schema = z.object({
  email: z.string()
    .email("Por favor, introduce un email válido"),
  consent: z.literal('on', {
    errorMap: () => ({ message: "Debes aceptar los términos y condiciones" }),
  }),
  category: z.string().optional(),
  city: z.string().optional(),
});

export function NewsletterForm({ title = 'Suscríbete a nuestra newsletter' }: { title?: string }) {
  const location = useLocation();
  const newsletterFetcher = useFetcher<SerializeFrom<NewsletterSubscribeResponse>>();
  const isSuccess = newsletterFetcher.data?.success;
  const subscriberStatus = newsletterFetcher.data?.status;
  const isSubmitting = newsletterFetcher.state === 'submitting' || newsletterFetcher.state === 'loading';

  const { data: categories } = useCategories();
  const [showConsent, setShowConsent] = useState(false);

  // Parse the pathname to extract category and city
  const pathParts = location.pathname.split('/').filter(Boolean);
  const category = pathParts[0];
  const city = pathParts[1];
  const categoryTitle = categories?.find(cat => cat.value === category)?.label;

  const [form, fields] = useForm({
    id: "newsletter_form",
    constraint: getZodConstraint(schema),
    defaultValue: {
      category,
      city,
    },
    onValidate({ formData }) {
      return parseWithZod(formData, { schema });
    },
    shouldValidate: "onBlur",
  });

  const getSuccessMessage = () => {
    if (subscriberStatus === 'pending') {
      return 'Te hemos enviado un email de confirmación. Por favor, revisa tu bandeja de entrada.';
    }
    return 'Tu correo ha sido agregado a la lista con éxito.';
  };

  return (
    <div className="w-full max-w-md mx-auto space-y-4">
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">
          {category && city 
            ? `Recibe las últimas novedades sobre ${categoryTitle || category} en ${city}`
            : "Recibe las últimas novedades sobre espacios y ofertas especiales"}
        </p>
      </div>
      <newsletterFetcher.Form
        method="POST"
        action="/resources/signin-newsletter"
        className="space-y-4"
        {...getFormProps(form)}
      >
        <AuthenticityTokenInput />
        {/* Hidden inputs for category and city */}
        {category && <input type="hidden" name="category" value={category} />}
        {city && <input type="hidden" name="city" value={city} />}
        
        <Field
          labelProps={{ 
            htmlFor: fields.email.id, 
            children: 'Email' 
          }}
          inputProps={{
            ...getInputProps(fields.email, {
              type: 'email',
              placeholder: 'tu@email.com',
            }),
            disabled: isSuccess || isSubmitting,
            onFocus: () => setShowConsent(true)
          }}
          errors={fields.email.errors}
        />

        {/* Render checkbox only when showConsent is true */}
        {showConsent && (
          <div className="animate-in fade-in slide-in-from-top-2 duration-300">
            <CheckboxField
              labelProps={{
                children: (
                  <>
                    Acepto recibir newsletters y comunicaciones comerciales.{' '}
                    <a 
                      href="/politica-de-privacidad" 
                      className="underline hover:text-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver política de privacidad
                    </a>
                  </>
                ),
              }}
              buttonProps={{
                ...getInputProps(fields.consent, { type: 'checkbox' }),
                name: 'consent',
                form: form.id,
                disabled: isSuccess || isSubmitting,
              }}
              errors={fields.consent.errors}
              className="mt-4"
            />
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting || isSuccess}
        >
          {isSuccess ? (
            <span>¡Gracias por suscribirte!</span>
          ) : isSubmitting ? (
            <span>Suscribiendo...</span>
          ) : (
            <>
              Suscribirse
              <Send className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </newsletterFetcher.Form>

      {isSuccess && (
        <p className="text-sm text-center text-muted-foreground animate-in fade-in slide-in-from-top-2 duration-300">
          {getSuccessMessage()}
        </p>
      )}
    </div>
  );
}
